<template>
  <div class="job-calendar" :class="{ 'mobile-view': isMobile }">
    <!-- Desktop View -->
    <div v-if="!isMobile">
      <div class="calendar-header">
        <button class="nav-btn" @click="previousMonth">
          <img src="/img/chevron-left-gray.svg" alt="previous" width="20" />
        </button>
        <h3 class="current-month">{{ currentMonthYear }}</h3>
        <button class="nav-btn" @click="nextMonth">
          <img src="/img/chevron-left-gray.svg" alt="next" width="20" style="transform: rotate(180deg)" />
        </button>
        <button class="today-btn" @click="goToToday">Today</button>
      </div>

      <div class="calendar-container">
        <div class="calendar-grid">
          <div class="calendar-day-header" v-for="day in weekDays" :key="day">
            {{ day }}
          </div>

          <div
            v-for="(day, index) in calendarDays"
            :key="index"
            class="calendar-day"
            :class="{
              'other-month': !day.isCurrentMonth,
              'today': day.isToday
            }"
          >
            <div class="day-number-container">
              <div class="day-number">{{ day.date }}</div>
            </div>
          </div>
        </div>

        <!-- Multi-day events overlay -->
        <div class="events-overlay">
          <div
            v-for="(event, eventIndex) in positionedEvents"
            :key="`${event.job.id}-${event.week}-${event.row}-${eventIndex}`"
            class="calendar-event"
            :class="getJobClass(event.job)"
            :style="getEventStyle(event)"
            @click="viewJob(event.job)"
            :title="event.job.name"
          >
            <div class="event-content">
              <img :src="getJobStatusIcon(event.job)" width="12" alt="status" class="event-icon" />
              <span class="event-name">{{ event.job.name }}</span>
              <JobAssigneeAvatars
                v-if="getJobAssigneeIds(event.job).length"
                :assignees="getJobAssigneeIds(event.job)"
                :users="accountUsers"
                :size="16"
                class="event-assignee-avatar"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile View -->
    <div v-else class="mobile-calendar">
      <!-- Month Selector -->
      <div class="mobile-header">
        <div class="mobile-month-selector">
          <button class="mobile-nav-btn" @click="previousMonth">
            <img src="/img/chevron-left-gray.svg" alt="previous" width="16" />
          </button>
          <h3 class="mobile-month-title">{{ currentMonthYear }}</h3>
          <button class="mobile-nav-btn" @click="nextMonth">
            <img src="/img/chevron-left-gray.svg" alt="next" width="16" style="transform: rotate(180deg)" />
          </button>
        </div>
      </div>

      <!-- Compact Month Grid -->
      <div class="mobile-month-grid">
        <div class="mobile-day-header" v-for="day in weekDays" :key="day">
          {{ day.charAt(0) }}
        </div>

        <div
          v-for="(day, index) in calendarDays"
          :key="index"
          class="mobile-day"
          :class="{
            'other-month': !day.isCurrentMonth,
            'today': day.isToday,
            'selected': isSelectedDate(day)
          }"
          @click="selectDate(day)"
        >
          <div class="mobile-day-number">{{ day.date }}</div>
          <div class="mobile-day-indicator" v-if="getDayEventCount(day) > 0"></div>
        </div>
      </div>

      <!-- Selected Day Events -->
      <div class="mobile-events-section">
        <div class="mobile-events-header">
          <h4 class="mobile-events-date">{{ selectedDateFormatted }}</h4>
          <span class="mobile-events-count">{{ selectedDateEvents.length }} event{{ selectedDateEvents.length !== 1 ? 's' : '' }}</span>
        </div>

        <div class="mobile-events-list">
          <div
            v-for="job in selectedDateEvents"
            :key="job.id"
            class="mobile-event-item"
            :class="getJobClass(job)"
            @click="viewJob(job)"
          >
            <div class="mobile-event-content">
              <img :src="getJobStatusIcon(job)" width="16" alt="status" class="mobile-event-icon" />
              <div class="mobile-event-details">
                <div class="mobile-event-name">{{ job.name }}</div>
                <div class="mobile-event-dates" v-if="job.startDate || job.endDate">
                  {{ formatEventDates(job) }}
                </div>
              </div>
              <JobAssigneeAvatars
                v-if="getJobAssigneeIds(job).length"
                :assignees="getJobAssigneeIds(job)"
                :users="accountUsers"
                :size="24"
                class="mobile-event-assignee"
              />
            </div>
          </div>

          <div v-if="selectedDateEvents.length === 0" class="mobile-no-events">
            No events for this day
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { JOB_STATUS_META, JOB_PRIORITY_META } from "../../utils/constants";
import { getJobAssigneeIds } from "../../utils/helpers";
import JobAssigneeAvatars from "./job-assignee-avatars.vue";

export default {
  name: "JobCalendar",
  components: {
    JobAssigneeAvatars
  },
  props: {
    jobs: {
      type: Array,
      default: () => []
    },
    jobStatuses: {
      type: Array,
      default: () => []
    },
    accountUsers: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      currentDate: new Date(),
      selectedDate: new Date(),
      weekDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      isMobile: false
    };
  },
  mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile);
  },
  computed: {
    currentMonthYear() {
      return this.currentDate.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
      });
    },
    calendarDays() {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();

      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const daysInMonth = lastDay.getDate();
      const startingDayOfWeek = firstDay.getDay();

      const days = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Previous month days
      const prevMonthLastDay = new Date(year, month, 0).getDate();
      for (let i = startingDayOfWeek - 1; i >= 0; i--) {
        const date = prevMonthLastDay - i;
        days.push({
          date,
          isCurrentMonth: false,
          isToday: false,
          fullDate: new Date(year, month - 1, date)
        });
      }

      // Current month days
      for (let date = 1; date <= daysInMonth; date++) {
        const fullDate = new Date(year, month, date);
        const isToday = fullDate.getTime() === today.getTime();

        days.push({
          date,
          isCurrentMonth: true,
          isToday,
          fullDate
        });
      }

      // Next month days to fill the grid
      const remainingDays = 42 - days.length; // 6 rows * 7 days
      for (let date = 1; date <= remainingDays; date++) {
        days.push({
          date,
          isCurrentMonth: false,
          isToday: false,
          fullDate: new Date(year, month + 1, date)
        });
      }

      return days;
    },
    positionedEvents() {
      if (!this.jobs || !Array.isArray(this.jobs) || this.calendarDays.length === 0) {
        return [];
      }

      // Get all jobs with valid date ranges
      const jobEvents = this.jobs
        .filter(job => job && (job.startDate || job.endDate))
        .map(job => {
          const startDate = job.startDate ? new Date(job.startDate) : null;
          const endDate = job.endDate ? new Date(job.endDate) : null;

          if (startDate) startDate.setHours(0, 0, 0, 0);
          if (endDate) endDate.setHours(0, 0, 0, 0);

          // If only one date is set, use it for both
          const eventStart = startDate || endDate;
          const eventEnd = endDate || startDate;

          return {
            job,
            startDate: eventStart,
            endDate: eventEnd
          };
        });

      // Calculate positioning for each event
      const positioned = [];
      const weeks = Math.ceil(this.calendarDays.length / 7);

      for (let week = 0; week < weeks; week++) {
        const weekStart = week * 7;
        const weekEnd = weekStart + 7;
        const weekDays = this.calendarDays.slice(weekStart, weekEnd);

        // Get events that appear in this week
        const weekEvents = jobEvents.filter(event => {
          return weekDays.some(day => {
            const dayTime = day.fullDate.getTime();
            return dayTime >= event.startDate.getTime() && dayTime <= event.endDate.getTime();
          });
        });

        // Sort by start date, then by duration (longer events first)
        weekEvents.sort((a, b) => {
          const startDiff = a.startDate.getTime() - b.startDate.getTime();
          if (startDiff !== 0) return startDiff;
          const aDuration = a.endDate.getTime() - a.startDate.getTime();
          const bDuration = b.endDate.getTime() - b.startDate.getTime();
          return bDuration - aDuration;
        });

        // Assign rows to avoid overlaps
        const rows = [];
        weekEvents.forEach(event => {
          let rowIndex = 0;

          // Find the first available row
          while (rows[rowIndex]) {
            const hasOverlap = rows[rowIndex].some(e => {
              return !(e.endDate.getTime() < event.startDate.getTime() ||
                       e.startDate.getTime() > event.endDate.getTime());
            });
            if (!hasOverlap) break;
            rowIndex++;
          }

          if (!rows[rowIndex]) rows[rowIndex] = [];
          rows[rowIndex].push(event);

          // Calculate positioning for this week segment
          const startIdx = weekDays.findIndex(day =>
            day.fullDate.getTime() >= event.startDate.getTime()
          );
          const endIdx = weekDays.findIndex(day =>
            day.fullDate.getTime() > event.endDate.getTime()
          );

          const dayStart = startIdx >= 0 ? startIdx : 0;
          const dayEnd = endIdx >= 0 ? endIdx : 7;
          const span = dayEnd - dayStart;

          if (span > 0) {
            positioned.push({
              job: event.job,
              week,
              row: rowIndex,
              dayStart,
              span
            });
          }
        });
      }

      return positioned;
    },
    selectedDateEvents() {
      if (!this.jobs || !Array.isArray(this.jobs)) {
        return [];
      }

      const selectedTime = this.selectedDate.getTime();

      return this.jobs.filter(job => {
        if (!job || (!job.startDate && !job.endDate)) return false;

        const startDate = job.startDate ? new Date(job.startDate) : null;
        const endDate = job.endDate ? new Date(job.endDate) : null;

        if (startDate) startDate.setHours(0, 0, 0, 0);
        if (endDate) endDate.setHours(0, 0, 0, 0);

        const eventStart = startDate || endDate;
        const eventEnd = endDate || startDate;

        return selectedTime >= eventStart.getTime() && selectedTime <= eventEnd.getTime();
      }).sort((a, b) => {
        const aStart = a.startDate ? new Date(a.startDate).getTime() : 0;
        const bStart = b.startDate ? new Date(b.startDate).getTime() : 0;
        return aStart - bStart;
      });
    },
    selectedDateFormatted() {
      const options = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };
      return this.selectedDate.toLocaleDateString('en-US', options);
    }
  },
  methods: {
    getEventStyle(event) {
      const dayHeaderHeight = 40; // Height of day headers
      const dayHeight = 120; // Height of each calendar day cell
      const eventHeight = 24; // Height of each event bar
      const eventMargin = 2; // Margin between events
      const dateNumberHeight = 32; // Height of date number container
      const eventStartPadding = 2; // Small padding from date number

      const top = dayHeaderHeight + (event.week * dayHeight) + dateNumberHeight + eventStartPadding + (event.row * (eventHeight + eventMargin));
      const left = `${(event.dayStart / 7) * 100}%`;
      const width = `calc(${(event.span / 7) * 100}% - 4px)`;

      return {
        position: 'absolute',
        top: `${top}px`,
        left: left,
        width: width,
        height: `${eventHeight}px`,
        zIndex: 10
      };
    },
    getJobClass(job) {
      const status = this.jobStatuses.find(s => s.id === job.status);
      const priority = job.priority || 0;

      return {
        'priority-urgent': priority === 4,
        'priority-high': priority === 3,
        'priority-medium': priority === 2,
        'priority-low': priority === 1
      };
    },
    getJobStatusIcon(job) {
      const status = this.jobStatuses.find(s => s.id === job.status);
      if (status && status.icon) {
        return status.icon;
      }
      return JOB_STATUS_META[status?.statusCategoryID]?.icon || '/img/block.svg';
    },
    getJobAssigneeIds,
    viewJob(job) {
      this.$emit('view-job', job);
    },
    previousMonth() {
      this.currentDate = new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth() - 1,
        1
      );
    },
    nextMonth() {
      this.currentDate = new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth() + 1,
        1
      );
    },
    goToToday() {
      this.currentDate = new Date();
      this.selectedDate = new Date();
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
    selectDate(day) {
      this.selectedDate = new Date(day.fullDate);
    },
    isSelectedDate(day) {
      const dayTime = day.fullDate.getTime();
      const selectedTime = this.selectedDate.getTime();
      return dayTime === selectedTime;
    },
    getDayEventCount(day) {
      if (!this.jobs || !Array.isArray(this.jobs)) return 0;

      const dayTime = day.fullDate.getTime();

      return this.jobs.filter(job => {
        if (!job || (!job.startDate && !job.endDate)) return false;

        const startDate = job.startDate ? new Date(job.startDate) : null;
        const endDate = job.endDate ? new Date(job.endDate) : null;

        if (startDate) startDate.setHours(0, 0, 0, 0);
        if (endDate) endDate.setHours(0, 0, 0, 0);

        const eventStart = startDate || endDate;
        const eventEnd = endDate || startDate;

        return dayTime >= eventStart.getTime() && dayTime <= eventEnd.getTime();
      }).length;
    },
    formatEventDates(job) {
      const formatDate = (date) => {
        const d = new Date(date);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      };

      if (job.startDate && job.endDate) {
        const start = formatDate(job.startDate);
        const end = formatDate(job.endDate);
        return `${start} - ${end}`;
      } else if (job.startDate) {
        return formatDate(job.startDate);
      } else if (job.endDate) {
        return formatDate(job.endDate);
      }
      return '';
    }
  }
};
</script>

<style scoped>
.job-calendar {
  padding: 20px;
  background: #ffffff;
  min-height: calc(100vh - 180px);
}

.calendar-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  margin-bottom: 24px;
}

.nav-btn {
  background: transparent;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  height: 38px;
}

.nav-btn:hover {
  background-color: #f8f9fa;
}

.today-btn {
  background: transparent;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #495057;
  transition: background-color 0.2s;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.today-btn:hover {
  background-color: #f8f9fa;
}

.current-month {
  font-size: 20px;
  font-weight: 600;
  color: #212529;
  margin: 0;
}

.calendar-container {
  position: relative;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background-color: #dee2e6;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: visible;
}

.calendar-day-header {
  background-color: #f8f9fa;
  padding: 12px 8px;
  text-align: center;
  font-weight: 600;
  font-size: 14px;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.calendar-day {
  background-color: #ffffff;
  min-height: 120px;
  height: 120px;
  padding: 0;
  position: relative;
}

.calendar-day.other-month {
  background-color: #f8f9fa;
  opacity: 0.5;
}

.calendar-day.today {
  background-color: #e7f3ff;
}

.day-number-container {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 4px 8px;
  min-height: 32px;
  position: relative;
  z-index: 15;
}

.day-number {
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: transparent;
  border-radius: 50%;
  min-width: 26px;
  height: 26px;
  text-align: center;
}

.calendar-day.today .day-number {
  color: #ffffff;
  background-color: #0066cc;
}

.events-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  pointer-events: none;
}

.calendar-event {
  pointer-events: all;
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 2px 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 12px;
  border-left: 3px solid #adb5bd;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  margin: 0 2px;
}

.calendar-event:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
  z-index: 20 !important;
}

.calendar-event.priority-urgent {
  border-left-color: #dc2626;
  background-color: #fef2f2;
}

.calendar-event.priority-high {
  border-left-color: #f59e0b;
  background-color: #fffbeb;
}

.calendar-event.priority-medium {
  border-left-color: #3b82f6;
  background-color: #eff6ff;
}

.calendar-event.priority-low {
  border-left-color: #10b981;
  background-color: #f0fdf4;
}

.event-content {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 100%;
}

.event-icon {
  flex-shrink: 0;
}

.event-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #212529;
  font-weight: 500;
  line-height: 1.4;
}

.event-assignee-avatar {
  flex-shrink: 0;
}

/* Mobile Styles */
.mobile-view {
  padding: 0 !important;
  background: #f8f9fa;
  min-height: auto !important;
  overflow: hidden;
}

.mobile-calendar {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: calc(100vh - 180px);
  background: #ffffff;
  overflow: hidden;
}

.mobile-header {
  background: #ffffff;
  border-bottom: 1px solid #dee2e6;
  padding: 16px;
  flex-shrink: 0;
}

.mobile-month-selector {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.mobile-month-title {
  font-size: 18px;
  font-weight: 600;
  color: #212529;
  margin: 0;
}

.mobile-nav-btn {
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.mobile-nav-btn:hover {
  background-color: #f8f9fa;
}

.mobile-month-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: #ffffff;
  border-bottom: 1px solid #dee2e6;
  padding: 8px;
  gap: 4px;
  flex-shrink: 0;
}

.mobile-day-header {
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: #6c757d;
  padding: 4px 0;
  text-transform: uppercase;
}

.mobile-day {
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  position: relative;
  transition: background-color 0.2s;
}

.mobile-day:active {
  background-color: #e9ecef;
}

.mobile-day.other-month .mobile-day-number {
  color: #adb5bd;
}

.mobile-day.today {
  background-color: #e7f3ff;
}

.mobile-day.selected {
  background-color: #0066cc;
}

.mobile-day.selected .mobile-day-number {
  color: #ffffff;
  font-weight: 700;
}

.mobile-day-number {
  font-size: 14px;
  font-weight: 500;
  color: #212529;
}

.mobile-day-indicator {
  width: 4px;
  height: 4px;
  background-color: #0066cc;
  border-radius: 50%;
  position: absolute;
  bottom: 2px;
}

.mobile-day.selected .mobile-day-indicator {
  background-color: #ffffff;
}

.mobile-events-section {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: #f8f9fa;
  min-height: 0;
}

.mobile-events-header {
  background: #ffffff;
  padding: 16px;
  border-bottom: 1px solid #dee2e6;
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
}

.mobile-events-date {
  font-size: 16px;
  font-weight: 600;
  color: #212529;
  margin: 0 0 4px 0;
}

.mobile-events-count {
  font-size: 13px;
  color: #6c757d;
}

.mobile-events-list {
  padding: 12px;
}

.mobile-event-item {
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  margin-bottom: 8px;
  border-left: 4px solid #adb5bd;
  transition: box-shadow 0.2s;
}

.mobile-event-item:active {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.mobile-event-item.priority-urgent {
  border-left-color: #dc2626;
}

.mobile-event-item.priority-high {
  border-left-color: #f59e0b;
}

.mobile-event-item.priority-medium {
  border-left-color: #3b82f6;
}

.mobile-event-item.priority-low {
  border-left-color: #10b981;
}

.mobile-event-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
}

.mobile-event-icon {
  flex-shrink: 0;
}

.mobile-event-details {
  flex: 1;
  min-width: 0;
}

.mobile-event-name {
  font-size: 15px;
  font-weight: 500;
  color: #212529;
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-event-dates {
  font-size: 13px;
  color: #6c757d;
}

.mobile-event-assignee {
  flex-shrink: 0;
}

.mobile-no-events {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
  font-size: 14px;
}

@media (max-width: 768px) {
  .calendar-day {
    min-height: 80px;
    height: 80px;
  }

  .event-name {
    display: none;
  }

  .calendar-event {
    padding: 2px 4px;
  }
}
</style>
